import { useState, useRef, useCallback, useEffect } from "react";
import { Paperclip, ArrowUp, X, FileText, Image as ImageIcon } from "@phosphor-icons/react";
import { uploadFile } from "../lib/api";
import { toast } from "sonner";
import { useDraft } from "../hooks/useDraft";

const ACCEPTED = ".png,.jpg,.jpeg,.gif,.webp,.heic,.pdf,.txt,.md,.csv,.json,.docx";

function fileIcon(ct) {
  if (ct?.startsWith("image/")) return ImageIcon;
  return FileText;
}

export default function ChatInput({ onSend, disabled, draftKey }) {
  const [draft, setDraft, clearDraft] = useDraft(
    draftKey ? `chat:${draftKey}` : null,
    { text: "", files: [] }
  );
  const text = draft.text;
  const files = draft.files;
  const setText = useCallback(
    (next) =>
      setDraft((d) => ({
        ...d,
        text: typeof next === "function" ? next(d.text) : next,
      })),
    [setDraft]
  );
  const setFiles = useCallback(
    (next) =>
      setDraft((d) => ({
        ...d,
        files: typeof next === "function" ? next(d.files) : next,
      })),
    [setDraft]
  );

  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const taRef = useRef(null);

  const adjustHeight = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 200) + "px";
  };

  // Adjust textarea height after restored draft renders
  useEffect(() => {
    requestAnimationFrame(adjustHeight);
  }, [text]);

  const handleFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(fileList)) {
        try {
          const meta = await uploadFile(f);
          uploaded.push(meta);
        } catch (e) {
          toast.error(`Upload failed: ${f.name}`);
        }
      }
      setFiles((prev) => [...prev, ...uploaded]);
    } finally {
      setUploading(false);
    }
  }, []);

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const submit = async () => {
    const t = text.trim();
    if (!t && files.length === 0) return;
    if (disabled) return;
    onSend(t, files.map((f) => f.id), files);
    setDraft({ text: "", files: [] });
    clearDraft();
    requestAnimationFrame(adjustHeight);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      className={`relative px-3 sm:px-6 pb-3 sm:pb-6 pt-2 safe-x safe-bottom ${
        dragOver ? "ring-2 ring-cyan-500/40 bg-cyan-500/5" : ""
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      data-testid="chat-input-zone"
    >
      <div className="max-w-3xl mx-auto">
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 px-1">
            {files.map((f) => {
              const Icon = fileIcon(f.content_type);
              return (
                <div
                  key={f.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs"
                  data-testid={`file-pill-${f.id}`}
                >
                  <Icon size={14} weight="duotone" className="text-cyan-400" />
                  <span className="max-w-[180px] truncate">{f.original_filename}</span>
                  <button
                    onClick={() => removeFile(f.id)}
                    className="text-white/50 hover:text-white p-2 -m-1.5"
                    aria-label="Remove file"
                    data-testid={`file-remove-${f.id}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="glass rounded-3xl px-2 py-2 flex items-end gap-1.5 shadow-xl shadow-black/40">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPTED}
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="file-input"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
            aria-label="Attach file"
            data-testid="attach-button"
          >
            <Paperclip size={18} weight="bold" />
          </button>
          <textarea
            ref={taRef}
            rows={1}
            placeholder="Ask Journey anything…"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              adjustHeight();
            }}
            onKeyDown={onKey}
            disabled={disabled}
            className="flex-1 resize-none bg-transparent outline-none px-2 py-2.5 text-[15px] placeholder:text-white/40 max-h-[200px]"
            data-testid="chat-textarea"
          />
          <button
            type="button"
            onClick={submit}
            disabled={disabled || uploading || (!text.trim() && files.length === 0)}
            className="shrink-0 w-11 h-11 rounded-full bg-cyan-500 text-black flex items-center justify-center disabled:bg-white/10 disabled:text-white/30 hover:bg-cyan-400 transition-colors active:scale-95"
            aria-label="Send"
            data-testid="send-button"
          >
            <ArrowUp size={18} weight="bold" />
          </button>
        </div>
        <p className="text-[11px] text-white/30 text-center mt-2">
          Drop files here · Images, PDFs, DOCX, TXT supported
        </p>
      </div>
    </div>
  );
}
